import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { Observable, map, throwError } from 'rxjs';
import { AuthService } from '../../../app/auth/auth.service';
import { environment } from '../../../environments/environment';
import { CompanyContextService } from '../../company-context.service';
import { AgedDebtBucketItem, ClientChaseListDrillDownDetailRow, CreditControlClientRow } from '../dashboard/dashboard.service';

interface CurrentAgedDebtBucketRow {
  ResultSetOrder?: number | null;
  DisplayOrder?: number | null;
  Bucket?: string | null;
  DocumentCount?: number | string | null;
  Amount?: number | string | null;
}

interface CurrentAgedDebtClientRow {
  ResultSetOrder?: number | null;
  RowNumber?: number | null;
  ContactId?: string | null;
  ClientName?: string | null;
  OutstandingInvoiceCount?: number | null;
  TotalOutstanding?: number | string | null;
  OutstandingVatIncluded?: number | string | null;
  Bucket0To30?: number | string | null;
  Bucket31To60?: number | string | null;
  Bucket61To90?: number | string | null;
  Bucket90Plus?: number | string | null;
  TotalClients?: number | null;
}

interface UnallocatedCashSourceApiRow {
  ResultSetOrder?: number | null;
  OverpaymentId?: string | null;
  ContactId?: string | null;
  ClientName?: string | null;
  SourceType?: string | null;
  TransactionDate?: string | null;
  Reference?: string | null;
  DocumentStatus?: string | null;
  SourceCurrencyCode?: string | null;
  CurrencyRate?: number | string | null;
  OriginalReceiptAmount?: number | string | null;
  AllocatedAmount?: number | string | null;
  RefundedAmount?: number | string | null;
  AppliedOrRefundedAmount?: number | string | null;
  UnallocatedCashAmount?: number | string | null;
  BankTransactionType?: string | null;
  BankTransactionDate?: string | null;
  BankTransactionStatus?: string | null;
  BankAccountCode?: string | null;
  BankAccountName?: string | null;
  BankReference?: string | null;
  BankTransactionTotalSourceCurrency?: number | string | null;
  IsReconciled?: boolean | number | null;
}

interface ClientChaseDrilldownRow {
  ResultSetOrder?: number | null;
  ContactId?: string | null;
  ClientName?: string | null;
  ItemType?: string | null;
  DocumentNumber?: string | null;
  Reference?: string | null;
  TransactionDate?: string | null;
  DueDate?: string | null;
  DocumentStatus?: string | null;
  ItemDetails?: string | null;
  OriginalAmount?: number | string | null;
  PaymentsApplied?: number | string | null;
  CreditNotesApplied?: number | string | null;
  OverpaymentsApplied?: number | string | null;
  PrepaymentsApplied?: number | string | null;
  OutstandingAmount?: number | string | null;
  UnallocatedCashAmount?: number | string | null;
  AgeInDays?: number | string | null;
  DaysOverdue?: number | string | null;
}

@Injectable({ providedIn: 'root' })
export class CurrentAgedDebtService {
  private readonly http = inject(HttpClient);
  private readonly auth = inject(AuthService);
  private readonly companyContext = inject(CompanyContextService);
  private readonly apiUrl = environment.currentAgedDebtApiUrl;
  private readonly drilldownApiUrl = environment.clientChaseListDrilldownApiUrl;

  getClientChaseDrilldown(
    contactId: string,
    selectedBucket: string,
    asOfDate?: string | null
  ): Observable<ClientChaseListDrillDownDetailRow[]> {
    const compId = this.resolveCompanyId();
    if (compId == null) {
      return throwError(() => new Error('A company must be selected before loading client chase details.'));
    }

    let params = new HttpParams()
      .set('contactId', contactId)
      .set('selectedBucket', selectedBucket)
      .set('compId', compId);
    if (asOfDate) {
      params = params.set('asOfDate', asOfDate);
    }

    return this.http.get<unknown>(this.drilldownApiUrl, { params }).pipe(
      map(response => this.findDrilldownRows(response)
        .filter(row => Number(row.ResultSetOrder) === 2)
        .map(row => ({
          contactId: row.ContactId ?? '',
          clientName: row.ClientName ?? '',
          invoiceNumber: row.DocumentNumber ?? '',
          invoiceDate: row.TransactionDate ?? '',
          dueDate: row.DueDate ?? '',
          invoiceStatus: row.DocumentStatus ?? '',
          invoiceDetails: row.ItemType === 'UNALLOCATED_CASH'
            ? row.Reference?.trim() || row.ItemDetails || ''
            : row.ItemDetails ?? row.Reference ?? '',
          itemType: row.ItemType ?? '',
          invoiceTotal: Number(row.OriginalAmount ?? 0),
          amountPaid: Number(row.PaymentsApplied ?? 0),
          creditNotesApplied: Number(row.CreditNotesApplied ?? 0),
          overpaymentsApplied: Number(row.OverpaymentsApplied ?? 0),
          prepaymentsApplied: Number(row.PrepaymentsApplied ?? 0),
          outstandingAmount: Number(row.OutstandingAmount ?? 0),
          unallocatedCashAmount: Number(row.UnallocatedCashAmount ?? 0),
          daysOverdue: row.DaysOverdue != null
            ? Math.max(0, Number(row.DaysOverdue))
            : this.calculateDaysOverdue(row.DueDate, asOfDate),
          invoiceAgeDays: Number(row.AgeInDays ?? 0),
        })))
    );
  }

  private calculateDaysOverdue(dueDate: string | null | undefined, asOfDate?: string | null): number {
    if (!dueDate) return 0;

    const isoDate = dueDate.match(/^(\d{4})-(\d{2})-(\d{2})/);
    const parsedDueDate = isoDate
      ? new Date(Number(isoDate[1]), Number(isoDate[2]) - 1, Number(isoDate[3]))
      : new Date(dueDate);
    if (Number.isNaN(parsedDueDate.getTime())) return 0;

    const calculationDate = asOfDate ? new Date(`${asOfDate}T00:00:00`) : new Date();
    calculationDate.setHours(0, 0, 0, 0);
    parsedDueDate.setHours(0, 0, 0, 0);
    return Math.max(0, Math.floor((calculationDate.getTime() - parsedDueDate.getTime()) / 86_400_000));
  }

  getCurrentAgedDebt(asOfDate?: string | null): Observable<CurrentAgedDebtResult> {
    const compId = this.resolveCompanyId();
    if (compId == null) {
      return throwError(() => new Error('A company must be selected before loading current aged debt.'));
    }

    let params = new HttpParams()
      .set('selectedBucket', 'ALL')
      .set('compId', compId);
    if (asOfDate) {
      params = params.set('asOfDate', asOfDate);
    }
    return this.http.get<unknown>(this.apiUrl, { params }).pipe(
      map(response => {
        const summaryRow = this.findSummaryRow(response);
        const clients = this.findClientRows(response)
          .filter(row => Number(row.ResultSetOrder) === 3)
          .sort((left, right) => Number(left.RowNumber ?? 0) - Number(right.RowNumber ?? 0))
          .map(row => ({
            rowNumber: Number(row.RowNumber ?? 0),
            contactId: row.ContactId ?? null,
            clientName: row.ClientName ?? '',
            outstandingInvoiceCount: Number(row.OutstandingInvoiceCount ?? 0),
            totalOutstanding: Number(row.TotalOutstanding ?? 0),
            billedLedgerAmount: Number(row.OutstandingVatIncluded ?? 0),
            bucket0To30: Number(row.Bucket0To30 ?? 0),
            bucket31To60: Number(row.Bucket31To60 ?? 0),
            bucket61To90: Number(row.Bucket61To90 ?? 0),
            bucket90Plus: Number(row.Bucket90Plus ?? 0),
            dso: null,
            totalClients: Number(row.TotalClients ?? 0),
          }))
          .filter(row => row.clientName !== '');
        const buckets = this.findBucketRows(response)
          .filter(row => Number(row.ResultSetOrder) === 2)
          .sort((left, right) => Number(left.DisplayOrder ?? 0) - Number(right.DisplayOrder ?? 0))
          .map(row => ({
            bucket: row.Bucket ?? '',
            invoiceCount: Number(row.DocumentCount ?? 0),
            amount: Number(row.Amount ?? 0),
          }))
          .filter(row => row.bucket !== '');

        const unallocatedCashRows = this.findUnallocatedCashRows(response)
          .filter(row => Number(row.ResultSetOrder) === 4)
          .map(row => ({
            clientName: row.ClientName ?? '',
            sourceType: row.SourceType ?? '',
            transactionDate: row.TransactionDate ?? '',
            reference: row.Reference ?? '',
            documentStatus: row.DocumentStatus ?? '',
            sourceCurrencyCode: row.SourceCurrencyCode ?? '',
            originalReceiptAmount: Number(row.OriginalReceiptAmount ?? 0),
            allocatedAmount: Number(row.AllocatedAmount ?? 0),
            refundedAmount: Number(row.RefundedAmount ?? 0),
            appliedOrRefundedAmount: Number(row.AppliedOrRefundedAmount ?? 0),
            unallocatedCashAmount: Number(row.UnallocatedCashAmount ?? 0),
            bankTransactionType: row.BankTransactionType ?? '',
            bankTransactionDate: row.BankTransactionDate ?? '',
            bankTransactionStatus: row.BankTransactionStatus ?? '',
            bankAccountName: row.BankAccountName ?? '',
            bankReference: row.BankReference ?? '',
            bankTransactionTotal: Number(row.BankTransactionTotalSourceCurrency ?? 0),
            isReconciled: row.IsReconciled == null ? '' : (row.IsReconciled ? 'Yes' : 'No'),
          }));

        return {
          buckets,
          clients,
          unallocatedCashRows,
          summary: summaryRow
            ? this.mapSummary(summaryRow)
            : null,
        };
      })
    );
  }

  private mapSummary(row: CurrentAgedDebtSummaryRow): CurrentAgedDebtSummary {
    const netDebtorBalance = Number(row.NetDebtorBalance ?? 0);
    const unallocatedCash = Number(row.UnallocatedCashOnAccount ?? 0);
    const controlAccountAdjustment = Number(row.ControlAccountAdjustment ?? 0);
    const apiTotalOutstanding = Number(row.TotalOutstanding ?? 0);
    const hasReconciliationValues = row.NetDebtorBalance != null
      && row.UnallocatedCashOnAccount != null
      && row.ControlAccountAdjustment != null;

    return {
      totalOutstanding: hasReconciliationValues
        ? netDebtorBalance + unallocatedCash + controlAccountAdjustment
        : apiTotalOutstanding,
      netDebtorBalance,
      unallocatedCash,
      controlAccountAdjustment,
    };
  }

  private findSummaryRow(value: unknown): CurrentAgedDebtSummaryRow | null {
    if (Array.isArray(value)) {
      const row = value.find(this.isSummaryRow);
      if (row) return row;
      for (const item of value) {
        const nestedRow = this.findSummaryRow(item);
        if (nestedRow) return nestedRow;
      }
      return null;
    }

    if (value && typeof value === 'object') {
      if (this.isSummaryRow(value)) return value;
      for (const nestedValue of Object.values(value)) {
        const nestedRow = this.findSummaryRow(nestedValue);
        if (nestedRow) return nestedRow;
      }
    }
    return null;
  }

  private findClientRows(value: unknown): CurrentAgedDebtClientRow[] {
    if (Array.isArray(value)) {
      const matchingRows = value.filter(this.isClientRow);
      if (matchingRows.length > 0) return matchingRows;
      for (const item of value) {
        const nestedRows = this.findClientRows(item);
        if (nestedRows.length > 0) return nestedRows;
      }
      return [];
    }

    if (value && typeof value === 'object') {
      for (const nestedValue of Object.values(value)) {
        const nestedRows = this.findClientRows(nestedValue);
        if (nestedRows.length > 0) return nestedRows;
      }
    }
    return [];
  }

  private findUnallocatedCashRows(value: unknown): UnallocatedCashSourceApiRow[] {
    if (Array.isArray(value)) {
      const matchingRows = value.filter(this.isUnallocatedCashRow);
      if (matchingRows.length > 0) return matchingRows;
      for (const item of value) {
        const nestedRows = this.findUnallocatedCashRows(item);
        if (nestedRows.length > 0) return nestedRows;
      }
      return [];
    }

    if (value && typeof value === 'object') {
      for (const nestedValue of Object.values(value)) {
        const nestedRows = this.findUnallocatedCashRows(nestedValue);
        if (nestedRows.length > 0) return nestedRows;
      }
    }
    return [];
  }

  private findDrilldownRows(value: unknown): ClientChaseDrilldownRow[] {
    if (Array.isArray(value)) {
      const matchingRows = value.filter(this.isDrilldownRow);
      if (matchingRows.length > 0) return matchingRows;
      for (const item of value) {
        const nestedRows = this.findDrilldownRows(item);
        if (nestedRows.length > 0) return nestedRows;
      }
      return [];
    }

    if (value && typeof value === 'object') {
      for (const nestedValue of Object.values(value)) {
        const nestedRows = this.findDrilldownRows(nestedValue);
        if (nestedRows.length > 0) return nestedRows;
      }
    }
    return [];
  }

  private findBucketRows(value: unknown): CurrentAgedDebtBucketRow[] {
    if (Array.isArray(value)) {
      const matchingRows = value.filter(this.isBucketRow);
      if (matchingRows.length > 0) return matchingRows;

      for (const item of value) {
        const nestedRows = this.findBucketRows(item);
        if (nestedRows.length > 0) return nestedRows;
      }
      return [];
    }

    if (value && typeof value === 'object') {
      for (const nestedValue of Object.values(value)) {
        const nestedRows = this.findBucketRows(nestedValue);
        if (nestedRows.length > 0) return nestedRows;
      }
    }

    return [];
  }

  private readonly isBucketRow = (value: unknown): value is CurrentAgedDebtBucketRow => {
    if (!value || typeof value !== 'object') return false;
    const row = value as Record<string, unknown>;
    return 'Bucket' in row && 'Amount' in row;
  };

  private readonly isSummaryRow = (value: unknown): value is CurrentAgedDebtSummaryRow => {
    if (!value || typeof value !== 'object') return false;
    const row = value as Record<string, unknown>;
    return Number(row['ResultSetOrder']) === 1
      && 'TotalOutstanding' in row
      && 'NetDebtorBalance' in row
      && 'UnallocatedCashOnAccount' in row;
  };

  private readonly isClientRow = (value: unknown): value is CurrentAgedDebtClientRow => {
    if (!value || typeof value !== 'object') return false;
    const row = value as Record<string, unknown>;
    return Number(row['ResultSetOrder']) === 3 && 'ClientName' in row && 'TotalOutstanding' in row;
  };

  private readonly isUnallocatedCashRow = (value: unknown): value is UnallocatedCashSourceApiRow => {
    if (!value || typeof value !== 'object') return false;
    const row = value as Record<string, unknown>;
    return Number(row['ResultSetOrder']) === 4 && 'UnallocatedCashAmount' in row;
  };

  private readonly isDrilldownRow =(value: unknown): value is ClientChaseDrilldownRow => {
    if (!value || typeof value !== 'object') return false;
    const row = value as Record<string, unknown>;
    return Number(row['ResultSetOrder']) === 2 && 'DocumentNumber' in row && 'OutstandingAmount' in row;
  };

  private resolveCompanyId(): number | null {
    const user = this.auth.session()?.user;
    if (!user) return null;

    return user.isSuperAdmin
      ? this.companyContext.getSelectedCompanyId(user.id) ?? user.compId
      : user.compId;
  }
}

interface CurrentAgedDebtSummaryRow {
  ResultSetOrder?: number | null;
  TotalOutstanding?: number | string | null;
  NetDebtorBalance?: number | string | null;
  UnallocatedCashOnAccount?: number | string | null;
  ControlAccountAdjustment?: number | string | null;
}

export interface CurrentAgedDebtSummary {
  totalOutstanding: number;
  netDebtorBalance: number;
  unallocatedCash: number;
  controlAccountAdjustment: number;
}

export interface UnallocatedCashSourceRow {
  clientName: string;
  sourceType: string;
  transactionDate: string;
  reference: string;
  documentStatus: string;
  sourceCurrencyCode: string;
  originalReceiptAmount: number;
  allocatedAmount: number;
  refundedAmount: number;
  appliedOrRefundedAmount: number;
  unallocatedCashAmount: number;
  bankTransactionType: string;
  bankTransactionDate: string;
  bankTransactionStatus: string;
  bankAccountName: string;
  bankReference: string;
  bankTransactionTotal: number;
  isReconciled: string;
}

export interface CurrentAgedDebtResult {
  buckets: AgedDebtBucketItem[];
  clients: CreditControlClientRow[];
  unallocatedCashRows: UnallocatedCashSourceRow[];
  summary: CurrentAgedDebtSummary | null;
}
